import React, { useMemo, useCallback, useState, useEffect } from 'react'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ButtonActionType, KYCStatus } from './index.type'
import type { ButtonItem } from './index.type'
import { useUserInfo, useElectronicCardInfo, useCardListStatus } from '@/hooks'
import { toFixedNoRound } from '@/utils'
import { Toast } from 'react-vant'
import RefreshList from '@/components/RefreshList'
import { APIWeb } from '@/api'
import { createAvatar } from '@dicebear/core'
import { identicon } from '@dicebear/collection'
import LogoTitle from '@/components/logoTitle'
import Ripple from '@/components/Ripple'

export default React.memo(function Home() {
  const { t } = useTranslation()
  const { userInfo, actions: userActions } = useUserInfo()
  const { cardInfo } = useElectronicCardInfo()

  const navigate = useNavigate()
  // 资产流水列表相关状态
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 页面加载时刷新用户信息
  useEffect(() => {
    userActions.refreshUserInfo()
  }, [])

  // const cardStates 

  // 通用的数据加载函数
  const fetchAssetFlowList = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loading) return

    setLoading(true)
    try {
      const response = await APIWeb.getCardRecords({
        card_id: cardInfo?.id,
        page,
        page_size: pageSize
      })
      if (response?.list) {
        const newData = response.list

        if (isAppend) {
          // 追加数据（用于加载更多）
          setData(prevData => [...prevData, ...newData])
        } else {
          // 替换数据（用于初始加载）
          setData(newData)
        }

        // 检查是否还有更多数据
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2) // 初始加载后，下次从第2页开始
          } else {
            setCurrentPage(prev => prev + 1) // 加载更多时，页码递增
          }
        }
      } else {
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取卡片记录失败:', error)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [cardInfo?.id, pageSize])

  // 加载更多数据
  const loadMore = useCallback(async () => {
    await fetchAssetFlowList(currentPage, true)
  }, [fetchAssetFlowList, currentPage])

  // 下拉刷新处理函数（仅刷新交易记录列表）
  const onRefresh = useCallback(async () => {
    // 直接重新加载第一页数据，fetchAssetFlowList 会自动处理状态
    await fetchAssetFlowList(1, false)
  }, [fetchAssetFlowList])

  const navigateToTransactionHistory = useCallback((item: any) => {
    if (item.noLine) return
    navigate('/account/transaction-history', {
      state: {
        coinName: item.name
      }
    })
  }, [navigate])

  // 处理卡片记录点击
  const handleRecordClick = useCallback((record: CardRecordItem, avatarSvg: string) => {
    navigate('/card/card-history-detail', {
      state: {
        record: {
          ...record,
          avatarSvg
        }
      }
    })
  }, [navigate])

  const buttonItems = useMemo<ButtonItem[]>(
    () => ([
      {
        actionType: ButtonActionType.Deposit,
        icon: '/icon/assets-icon1.svg',
        text: t('存款')
      },
      {
        actionType: ButtonActionType.Remittance,
        icon: '/icon/assets-icon2.svg',
        text: t('汇款')
      },
      {
        actionType: ButtonActionType.Exchange,
        icon: '/icon/assets-icon3.svg',
        text: t('兑换')
      },
      {
        actionType: ButtonActionType.More,
        icon: '/icon/assets-icon4.svg',
        text: t('更多')
      }
    ]),
    [t]
  )

  const cardItems = useMemo(
    () => ([
      { icon: '/images/home/usdc.png', name: 'USDC', text2: toFixedNoRound(userInfo?.usdc, 2), text3: `≈ ${toFixedNoRound(userInfo?.usdc, 2)} USD` },
      { icon: '/images/home/usdt.png', name: 'USDT', text2: toFixedNoRound(userInfo?.usdt, 2), text3: `≈ ${toFixedNoRound(userInfo?.usdt, 2)} USD` },
      { icon: '/images/home/usd1.png', name: 'USD1', text2: toFixedNoRound(userInfo?.usd1, 2), text3: `≈ ${toFixedNoRound(userInfo?.usd1, 2)} USD` },
    ]),
    [userInfo]
  )
  const { getCardListStatus } = useCardListStatus()

  const kycStatusText = useMemo(
    () => {
      switch (userInfo?.kyc_status) {
        case KYCStatus.WaitAudit:
          return t('您的KYC认证审核中')
        case KYCStatus.PassAudit:
          return t('您的KYC认证通过')
        case KYCStatus.Reject:
          return t('您的KYC认证失败')
        default:
          return t('需要完成KYC验证')
      }
    },
    [userInfo?.kyc_status, t]
  )

  const kycStatusButtonText = useMemo(
    () => {
      switch (userInfo?.kyc_status) {
        case KYCStatus.WaitAudit:
          return t('审核中')
        case KYCStatus.Reject:
          return t('重新去认证')
        default:
          return t('去认证')
      }
    },
    [userInfo?.kyc_status, t]
  )

  // 处理KYC按钮点击事件
  const handleKycButtonClick = useCallback(() => {
    const kycStatus = userInfo?.kyc_status

    switch (kycStatus) {
      case KYCStatus.WaitAudit:
        // 审核中状态，不允许进入KYC页面
        Toast.info({
          position: 'middle',
          message: t('您的KYC认证审核中'),
        })
        break
      case KYCStatus.PassAudit:
        // 已通过审核，不需要再次认证
        Toast.info({
          position: 'middle',
          message: t('您的KYC认证已通过无需重复认证'),
        })
        break
      case KYCStatus.Reject:
        // 审核被拒绝，允许重新认证
        navigate('/kyc-certification')
        break
      default:
        // 未认证状态，允许进入认证页面
        navigate('/kyc-certification')
        break
    }
  }, [userInfo?.kyc_status, navigate])

  const handleButtonPress = useCallback(
    (actionType: ButtonActionType) => {
      switch (actionType) {
        case ButtonActionType.Deposit:
          navigate('/recharge/select-currency')
          break
        case ButtonActionType.Remittance:
          navigate('/remittance/transfer')
          break
        case ButtonActionType.Exchange:
          navigate('/exchange/transfer')
          break
        case ButtonActionType.More:
          Toast.info(t('即将上线'))
          break
        default:
          break
      }
    },
    [navigate]
  )


  const CardItems = useMemo(
    () => cardItems.map((item, index) => (
      <Ripple key={index} className="rounded-2xl">
        <div className="bg-[#F5F5F5] p-3.5 min-w-31 min-h-21" onClick={() => navigateToTransactionHistory(item)}>
          <div className="flex items-center mb-2">
            <div className="w-6 h-6 rounded-full overflow-hidden">
              <img className="w-6 h-6" src={item.icon} />
            </div>
            <span className="text-xs font-medium ml-2 text-gray-800">{item.name}</span>
          </div>
          <div className="text-sm font-semibold text-gray-900">{item.text2}</div>
          <div className="text-xs text-gray-500">{item.text3}</div>
        </div>
      </Ripple>
    )),
    [cardItems]
  )

  const renderRecordItem = useCallback((record: any, index: number) => {
    // 为每个记录生成唯一的头像
    const avatarSeed = record.id || record.merch_name || `record-${index}`
    const avatarSvg = createAvatar(identicon, {
      seed: String(avatarSeed),
      size: 32,
      backgroundColor: ['#f0f0f0', '#e0e0e0', '#d0d0d0', '#c0c0c0'],
    }).toDataUri()

    return (
      <Ripple className='rounded-2'>
        <div className="flex items-center justify-between py-3 px-3.5" onClick={() => handleRecordClick(record, avatarSvg)}>
          <div className="flex w-8 h-8 bg-[#EAEEF6] rounded-full justify-center items-center mr-2 overflow-hidden flex-shrink-0">
            <img className="w-4 h-4" src={avatarSvg} alt="avatar" />
          </div>
          <div className='flex-1'>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-900">
                {record.merch_name || record.biz_type_string || 'Transaction'}
              </div>
              <div className="font-semibold text-xs text-black">
                {record.business_change ? (record.business_change > 0 ? `+${record.business_change}` : record.business_change) : '-'}
              </div>
            </div>

            <div className='flex items-center justify-between text-right'>
              <div className="text-2.5 text-[#979797] mt-2">
                {record.transaction_at_string || '-'}
              </div>
              <div className="font-normal text-xs text-[#808080] mt-2 break-all">
                {getCardListStatus(record.txn_type || '')}
              </div>
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [getCardListStatus, handleRecordClick])
  // Kyc
  const KycCertification = useMemo(
    () => (
      <div className="bg-white rounded-4 border-[0.5px] border-solid border-[#556AFF] mx-6 my-6 px-4 py-4  mb-0">
        <div className="flex items-center">
          <div className='w-4 h-4 bg-[#556AFF] rounded-full text-2 mr-4 text-white flex justify-center items-center'>i</div>
          <div className="flex-1">
            <div className="text-3.25 font-bold text-black mb-1">
              {kycStatusText}
            </div>
            <div className="text-3.25 text-gray-600 mb-1.5">
              {t('请完成KYC验证以继续使用全部功能')}
            </div>
            <button
              onClick={handleKycButtonClick}
              className="flex items-center text-[#556AFF] text-3.25 font-medium"
            >
              {kycStatusButtonText}
              <img src="/icon/go-right-icon.svg" alt="Arrow" className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    ),
    [kycStatusText, kycStatusButtonText, handleKycButtonClick]
  )

  const ButtonItems = useMemo(
    () => buttonItems.map((item, index) => (
      <Ripple className='rounded-lg' key={index}>
        <div
          key={index}
          className="flex flex-col items-center justify-center bg-white rounded-lg"
          onClick={() => handleButtonPress(item.actionType)}
        >
          <div className='w-13.5 h-13.5 flex items-center justify-center rounded-full bg-[#556AFF] mb-2'>
            <img src={item.icon} alt={item.text} />
          </div>
          <span className="text-3 text-[#2C2C2C]">{item.text}</span>
        </div>
      </Ripple>
    )),
    [buttonItems, handleButtonPress]
  )

  return (
    <PageLayout tabbarPage={true}>
      {/* Header */}
      <Header
        leftChildren={
          <Ripple className='rounded-full'>
            <div className='w-10 h-10 bg-[#E4E4E4]/50 flex items-center justify-center rounded-full' onClick={() => navigate('/my')}>
              <img src="/icon/user-icon.svg" alt="User Icon" />
            </div>
          </Ripple>
        }
        centerChildren={<LogoTitle />}
        rightChildren={
          <Ripple className='rounded-full'>
            <div className='w-10 h-10 bg-[#556AFF] flex items-center justify-center rounded-full' onClick={() => Toast.info(t('即将上线'))}>
              <img src="/icon/message-icon.svg" alt="Logo" />
            </div>
          </Ripple>
        }
      />

      {userInfo?.kyc_status !== KYCStatus.PassAudit ? KycCertification : null}

      {/* Assets */}
      <div className="bg-white  px-6 pt-6">
        <div className="text-base text-[#1A1A1A]/50 mb-1">{t('我的资产USD')}</div>
        <div className="text-8.75 font-bold text-black my-1">
          {toFixedNoRound(userInfo?.balance || 0, 2)}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between my-5  px-6 ">
        {ButtonItems}
      </div>

      {/* Currency Cards */}
      <div className="overflow-x-auto">
        <div className="inline-flex pb-2 px-6 space-x-3.5">
          {CardItems}
        </div>
      </div>
      {/* Records */}

      <div className='my-6 mx-6'>
        <div className="flex items-center justify-between mb-4">
          <span className="text-3.5 text-[#1A1A1A]">{t('交易记录')}</span>
        </div>
        <div className="bg-white py-3 pb-6 border-[0.5px] border-solid border-[0.5px] border-[#E5E5E5]  rounded-3xl">
          <div className="max-h-100 min-h-50 overflow-y-auto" style={{ touchAction: 'pan-y' }}>
            <RefreshList
              data={data}
              renderItem={renderRecordItem}
              keyExtractor={(item, index) => `${item.id}-${index}`}
              loading={loading}
              hasMore={hasMore}
              onRefresh={onRefresh}
              onLoadMore={loadMore}
              emptyText={t('暂无记录')}
              finishedText={t('没有更多数据了')}
              loadingText={t('加载中')}
            />
          </div>
        </div>
      </div>

    </PageLayout>
  )
})